/**
 * Brand detail page — header card + products table (primary surface) +
 * sizing rationale + decision history. The operator's main view for
 * deciding whether to pursue or reject a brand.
 *
 * Reads from GET /api/brands/:name (cache only — no Keepa cost). The
 * decision Save button calls PATCH /api/brands/:name/decision which writes
 * the operator decision row, then invalidates the brand + list queries.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft, Search } from 'lucide-react';
import { formatBrandReviewsLabel } from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '../design';
import {
  useBrandDecisionMutation,
  useBrandDetails,
  type BrandDetailsResponse,
} from '../lib/brands';
import { useDiscoverSuppliersForBrand } from '../lib/sourcing';

type Decision = 'selected' | 'rejected' | 'undecided';

const SIZE_LABELS = {
  micro: 'Micro',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
} as const;

function sizeBadgeVariant(size: keyof typeof SIZE_LABELS | null) {
  if (size === 'micro' || size === 'small') return 'positive';
  if (size === 'medium') return 'accent';
  return 'neutral';
}

function decisionBadgeVariant(d: Decision) {
  if (d === 'selected') return 'positive';
  if (d === 'rejected') return 'negative';
  return 'neutral';
}

function formatPrice(cents: number | null): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export function BrandDetailPage() {
  const { name } = useParams({ from: '/brands/$name' });
  const { data, isLoading, isError, error } = useBrandDetails(name);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-canvas text-fg-primary">
        <PageHeader name={name} />
        <main className="max-w-6xl mx-auto px-8 py-8">
          <p className="text-sm text-fg-secondary">Loading…</p>
        </main>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-canvas text-fg-primary">
        <PageHeader name={name} />
        <main className="max-w-6xl mx-auto px-8 py-8">
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              {(error as Error)?.message ?? 'Brand not found'}
            </p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <PageHeader name={data.name} />
      <main className="max-w-6xl mx-auto px-8 py-8 space-y-6">
        <HeaderCard brand={data} />
        {data.decision === 'selected' && <SourcingActionsCard brand={data} />}
        <ProductsTable brand={data} />
        {data.summary && <BrandKpisCard brand={data} />}
        <div className="grid gap-6 lg:grid-cols-2">
          <SizingRationaleCard brand={data} />
          <BrandMetaCard brand={data} />
        </div>
        <DecisionHistoryCard brand={data} />
      </main>
    </div>
  );
}

function PageHeader({ name }: { name: string }) {
  return (
    <header className="border-b border-border-subtle">
      <div className="max-w-6xl mx-auto px-8 py-4 flex items-center gap-3">
        <Link
          to="/brands"
          className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
        >
          <ChevronLeft className="size-5" />
        </Link>
        <div>
          <h1 className="text-xl font-medium leading-tight">{name}</h1>
          <p className="text-xs text-fg-tertiary">Brand detail</p>
        </div>
      </div>
    </header>
  );
}

function HeaderCard({ brand }: { brand: BrandDetailsResponse }) {
  const [decision, setDecision] = useState<Decision>(
    brand.decision === null ? 'undecided' : brand.decision
  );
  const [notes, setNotes] = useState(brand.decisionNotes ?? '');
  const mutate = useBrandDecisionMutation();

  useEffect(() => {
    setDecision(brand.decision === null ? 'undecided' : brand.decision);
    setNotes(brand.decisionNotes ?? '');
  }, [brand.decision, brand.decisionNotes]);

  const isDirty =
    decision !== (brand.decision === null ? 'undecided' : brand.decision) ||
    notes !== (brand.decisionNotes ?? '');

  async function save(): Promise<void> {
    await mutate.mutateAsync({
      name: brand.name,
      domain: brand.domain,
      decision: decision === 'undecided' ? null : decision,
      notes: notes.trim() || undefined,
    });
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="space-y-3 flex-1 min-w-[300px]">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-medium">{brand.name}</h2>
            {brand.sizeBucket && (
              <Badge variant={sizeBadgeVariant(brand.sizeBucket)}>
                {SIZE_LABELS[brand.sizeBucket]}
              </Badge>
            )}
            <Badge variant={decisionBadgeVariant(decision)}>
              {decision === 'selected'
                ? 'Selected'
                : decision === 'rejected'
                  ? 'Rejected'
                  : 'Undecided'}
            </Badge>
            <span className="text-xs text-fg-tertiary">Status: {brand.status}</span>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            <Stat label="Products" value={brand.productCount?.toLocaleString() ?? '—'} />
            <Stat
              label="Total reviews"
              value={formatBrandReviewsLabel({
                totalReviewCount: brand.totalReviewCount,
                sampledReviewSum: brand.sampledReviewSum,
                sampleSize: brand.sampleSize,
                productCount: brand.productCount,
              })}
            />
            <Stat label="Avg reviews" value={brand.avgReviewCount?.toLocaleString() ?? '—'} />
            <Stat
              label="Listing age"
              value={
                brand.oldestListedAt && brand.newestListedAt
                  ? `${new Date(brand.oldestListedAt).toLocaleDateString()} – ${new Date(
                      brand.newestListedAt
                    ).toLocaleDateString()}`
                  : '—'
              }
            />
          </dl>
        </div>
        <div className="space-y-3 w-full sm:w-[320px]">
          <FormField label="Decision" htmlFor="decision">
            <Select value={decision} onValueChange={(v) => setDecision(v as Decision)}>
              <SelectTrigger id="decision">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="undecided">Undecided</SelectItem>
                <SelectItem value="selected">Selected</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Notes" htmlFor="notes">
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
              rows={3}
              className={cn(
                'w-full px-3 py-2 rounded-sm border border-border-default bg-surface-0 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-accent-primary/40 focus:border-accent-primary'
              )}
              placeholder="Why selected / rejected? Any context for the next person to read this…"
            />
          </FormField>
          <div className="flex items-center justify-between">
            <span className="text-xs text-fg-tertiary">
              {brand.decidedAt &&
                `Last set by ${brand.decidedByEmail ?? 'unknown'} on ${new Date(
                  brand.decidedAt
                ).toLocaleDateString()}`}
            </span>
            <Button onClick={save} loading={mutate.isPending} disabled={!isDirty}>
              Save
            </Button>
          </div>
          {mutate.isError && (
            <p className="text-xs text-negative">{(mutate.error as Error).message}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function SourcingActionsCard({ brand }: { brand: BrandDetailsResponse }) {
  const [kickoffMsg, setKickoffMsg] = useState<string | null>(null);
  const [kickoffError, setKickoffError] = useState<string | null>(null);
  const discover = useDiscoverSuppliersForBrand();

  async function onDiscover() {
    setKickoffMsg(null);
    setKickoffError(null);
    try {
      const res = await discover.mutateAsync(brand.name);
      if (res.runs.length === 0) {
        setKickoffMsg(
          'No ranked products in cache yet. Wait for an NL search to surface ASINs for this brand.'
        );
      } else {
        setKickoffMsg(
          `Discovery kicked off for ${res.runs.length} ASIN${res.runs.length === 1 ? '' : 's'} — candidates will appear in the review queue.`
        );
      }
    } catch (e) {
      setKickoffError((e as Error).message ?? 'Discovery kickoff failed.');
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium">Sourcing</h3>
          <p className="text-xs text-fg-tertiary mt-1">
            Fan out supplier discovery over this brand&apos;s top 3 ASINs by sales rank.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={onDiscover}
            loading={discover.isPending}
            className="inline-flex items-center gap-2"
          >
            <Search className="size-4" />
            Discover suppliers
          </Button>
          {kickoffMsg && <p className="text-xs text-fg-secondary text-right">{kickoffMsg}</p>}
          {kickoffError && <p className="text-xs text-negative text-right">{kickoffError}</p>}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-fg-tertiary">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function ProductsTable({ brand }: { brand: BrandDetailsResponse }) {
  if (brand.recentProducts.length === 0) {
    return (
      <Card className="text-center py-10">
        <h3 className="text-sm font-medium mb-1">No cached products for this brand yet</h3>
        <p className="text-xs text-fg-tertiary max-w-md mx-auto">
          Products appear here as the brand-enrichment sweep deep-fetches them, or as your searches
          surface them.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden">
      <header className="px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-medium">
          Products under this brand{' '}
          <span className="text-fg-tertiary font-normal">({brand.recentProducts.length})</span>
        </h3>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-1 text-xs text-fg-tertiary">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">ASIN</th>
              <th className="text-left font-medium px-4 py-2.5">Title</th>
              <th className="text-left font-medium px-4 py-2.5">Category</th>
              <th className="text-right font-medium px-4 py-2.5 tabular-nums">Price</th>
              <th className="text-right font-medium px-4 py-2.5 tabular-nums">BSR</th>
              <th className="text-right font-medium px-4 py-2.5 tabular-nums">Reviews</th>
              <th className="text-left font-medium px-4 py-2.5">ROI</th>
            </tr>
          </thead>
          <tbody>
            {brand.recentProducts.map((p) => (
              <tr
                key={p.asin}
                className="border-t border-border-subtle hover:bg-surface-1/60 transition-colors duration-fast"
              >
                <td className="px-4 py-2.5 font-mono text-xs">
                  <Link
                    to="/products/$asin"
                    params={{ asin: p.asin }}
                    className="text-accent-fg hover:underline"
                  >
                    {p.asin}
                  </Link>
                </td>
                <td className="px-4 py-2.5 max-w-[280px] truncate" title={p.title ?? ''}>
                  {p.title ?? '—'}
                </td>
                <td className="px-4 py-2.5 text-fg-secondary text-xs">{p.category ?? '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {formatPrice(p.currentPriceCents)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {p.currentBsr?.toLocaleString() ?? '—'}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{p.reviewCount ?? '—'}</td>
                <td className="px-4 py-2.5">
                  {p.roiGrade ? (
                    <span className="inline-flex items-center gap-1">
                      <Badge variant="neutral">{p.roiGrade}</Badge>
                      {p.estimatedRoiPct && (
                        <span className="text-xs text-fg-tertiary tabular-nums">
                          {p.estimatedRoiPct}%
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-fg-tertiary">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function BrandKpisCard({ brand }: { brand: BrandDetailsResponse }) {
  // `summary` may be null on legacy deep-fetch-only rows; the parent guards
  // that case. Inside this component we can treat it as present.
  const s = brand.summary;
  if (!s) return null;

  const topSellers = s.topSellers ? Object.entries(s.topSellers).slice(0, 5) : [];
  const hasBsrRange = s.lowestRank != null || s.highestRank != null;

  return (
    <Card>
      <header className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="text-sm font-medium">Brand mix &amp; competition</h3>
        <span className="text-xs text-fg-tertiary">
          Updated {new Date(s.lastSummaryAt).toLocaleString()}
        </span>
      </header>
      <div className="grid gap-x-8 gap-y-2 sm:grid-cols-3 text-sm">
        <KpiRow label="Sellers competing" value={s.sellerCount?.toLocaleString() ?? '—'} />
        <KpiRow label="FBA share" value={formatPercent(s.isFbaPercent)} />
        <KpiRow label="Amazon as seller" value={formatPercent(s.soldByAmazonPercent)} />
        <KpiRow label="Avg buy box" value={formatPrice(s.avgBuyBoxCents)} />
        <KpiRow label="Avg buy box (90d)" value={formatPrice(s.avgBuyBox90Cents)} />
        <KpiRow label="Coupon presence" value={formatPercent(s.hasCouponPercent)} />
        <KpiRow
          label="Avg rating"
          value={s.avgRating != null ? `${s.avgRating.toFixed(1)} ★` : '—'}
        />
        <KpiRow
          label="BSR range"
          value={
            hasBsrRange
              ? `${s.lowestRank?.toLocaleString() ?? '?'} – ${s.highestRank?.toLocaleString() ?? '?'}`
              : '—'
          }
        />
      </div>
      {topSellers.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border-subtle">
          <p className="text-xs text-fg-tertiary mb-2">Top sellers (by ASIN count)</p>
          <ul className="flex flex-wrap gap-2">
            {topSellers.map(([seller, count]) => (
              <li key={seller}>
                <Badge variant="neutral">
                  {seller}{' '}
                  <span className="text-fg-tertiary tabular-nums">({count.toLocaleString()})</span>
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function KpiRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-fg-secondary text-xs">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function formatPercent(n: number | null): string {
  if (n == null) return '—';
  return `${n.toFixed(1)}%`;
}

function SizingRationaleCard({ brand }: { brand: BrandDetailsResponse }) {
  const r = brand.sizingRationale;
  return (
    <Card>
      <h3 className="text-sm font-medium mb-3">
        Why this brand is{' '}
        <span className="font-semibold">{r.bucket ? SIZE_LABELS[r.bucket] : 'unclassified'}</span>
      </h3>
      <dl className="space-y-2 text-sm">
        <RationaleRow
          label="Product count"
          value={r.observed.productCount?.toLocaleString() ?? '—'}
          hint="Brands with < 10 products are 'micro', < 50 are typically 'small', ≥ 500 are 'large'."
        />
        <RationaleRow
          label="Total reviews"
          value={r.observed.totalReviewCount?.toLocaleString() ?? '—'}
          hint="Sum across every brand product, capped at 200 ASINs (set when deep-fetched)."
        />
        <RationaleRow
          label="Sampled reviews"
          value={r.observed.sampledReviewSum?.toLocaleString() ?? '—'}
          hint="From the top-BSR sample probe taken during the initial search."
        />
        <RationaleRow
          label="Sample size"
          value={r.observed.sampleSize.toLocaleString()}
          hint="Number of ASINs hydrated when the aggregates above were computed."
        />
      </dl>
      <p className="text-xs text-fg-tertiary mt-3">
        Bucket thresholds: <span className="font-mono">micro &lt; 10</span> ·{' '}
        <span className="font-mono">small &lt; 50, reviews &lt; 10k</span> ·{' '}
        <span className="font-mono">medium &lt; 500, reviews &lt; 100k</span> ·{' '}
        <span className="font-mono">large ≥ 500 or reviews ≥ 100k</span>
      </p>
    </Card>
  );
}

function RationaleRow({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-secondary text-xs">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
      <div className="text-xs text-fg-tertiary flex-1 text-right truncate" title={hint}>
        {hint}
      </div>
    </div>
  );
}

function BrandMetaCard({ brand }: { brand: BrandDetailsResponse }) {
  return (
    <Card>
      <h3 className="text-sm font-medium mb-3">Cache + enrichment</h3>
      <dl className="space-y-2 text-sm">
        <MetaRow label="Status" value={brand.status} />
        <MetaRow
          label="Last deep-fetched"
          value={brand.deepFetchedAt ? new Date(brand.deepFetchedAt).toLocaleString() : '—'}
        />
        <MetaRow
          label="Oldest listing"
          value={brand.oldestListedAt ? new Date(brand.oldestListedAt).toLocaleDateString() : '—'}
        />
        <MetaRow
          label="Newest listing"
          value={brand.newestListedAt ? new Date(brand.newestListedAt).toLocaleDateString() : '—'}
        />
        <MetaRow label="Domain" value={String(brand.domain)} />
      </dl>
    </Card>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-fg-secondary text-xs">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function DecisionHistoryCard({ brand }: { brand: BrandDetailsResponse }) {
  if (brand.auditHistory.length === 0) {
    return (
      <Card>
        <h3 className="text-sm font-medium mb-2">Decision history</h3>
        <p className="text-xs text-fg-tertiary">
          No history yet — the operator hasn&apos;t set a decision or enrichment hasn&apos;t run.
        </p>
      </Card>
    );
  }
  return (
    <Card className="p-0 overflow-hidden">
      <header className="px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-medium">
          Decision history{' '}
          <span className="text-fg-tertiary font-normal">({brand.auditHistory.length})</span>
        </h3>
      </header>
      <ul className="divide-y divide-border-subtle">
        {brand.auditHistory.map((entry) => (
          <li key={entry.id} className="px-4 py-2.5 text-sm flex items-baseline gap-3">
            <span className="text-xs text-fg-tertiary tabular-nums whitespace-nowrap">
              {new Date(entry.occurredAt).toLocaleString()}
            </span>
            <span className="font-medium">{describeAuditAction(entry)}</span>
            {entry.userEmail && (
              <span className="text-xs text-fg-tertiary truncate">{entry.userEmail}</span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function describeAuditAction(entry: BrandDetailsResponse['auditHistory'][number]): string {
  if (entry.action === 'brand.decision_updated') {
    const after = (entry.changes?.after ?? null) as { decision?: string } | null;
    if (after?.decision) return `Marked ${after.decision}`;
    return 'Cleared decision';
  }
  if (entry.action === 'brand.enriched') {
    const c = entry.changes ?? {};
    const products = (c.productCount as number | undefined) ?? null;
    const reviews = (c.totalReviewCount as number | undefined) ?? null;
    return `Enriched — ${products ?? '?'} products, ${reviews ?? '?'} reviews`;
  }
  if (entry.action === 'brand.enrichment_skipped') {
    const reason = (entry.changes?.reason as string | undefined) ?? 'unknown';
    return `Enrichment skipped (${reason})`;
  }
  return entry.action;
}
